import React, { useEffect, useRef, useState } from 'react';
import { css } from 'emotion';
import { get } from 'lodash';
import moment from 'moment';
import autoAnimate from '@formkit/auto-animate';
import useFirebase from '../Firebase/useFirebase';
import { convertToBlob, downloadFile } from '../../utils/downloadFunctions';
import { DocumentButton } from '../components/DocumentButton';
import { documentsType, getDocumentName } from '../../constants/documents';
import { COVERMYTOOLS, BEAUTY } from '../../constants/products';
import arrowUp from '../../assets/angle-up.png';
import arrowDown from '../../assets/angle-down.png';

const policyButtonsContainer = css`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(13rem, 1fr));
  grid-gap: 1rem 0.5rem;
`;

const documentContainer = css`
  padding: 1.5rem;
  border-bottom: 0.5px solid var(--gray);
  @media (max-width: 767px) {
    padding: 1rem;
  }
`;

const documentTitle = css`
  font-weight: bold;
  font-size: 1.5rem;
  line-height: 1.875rem;
  letter-spacing: -0.03em;
  color: var(--dark);
  margin-bottom: 0.75rem;
`;

const sectionStyle = css`
  border-top: 0.5px solid var(--gray);
  &:last-child {
    border-bottom: 0.5px solid var(--gray);
  }
`;

const sectionHeaderStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 0;
  background-color: unset;
  border: none;
  cursor: pointer;
  width: 100%;
  text-align: left;
`;

const sectionTitleStyle = css`
  font-weight: 700;
  font-size: 1.125rem;
  line-height: 1.2em;
  letter-spacing: -0.03em;
  color: #242427;
  margin: 0;
`;

const sectionIconStyle = css`
  height: 1.5rem;
`;

const sectionBodyStyle = css`
  padding: 0 0 1.25rem 0;
`;

// The label a customer sees for a whole ICE transaction. A single transaction can
// carry documents of more than one type (a renewal acceptance also emits an NB
// wording, for example), so the section is named after the type that dominates it.
const TRANSACTION_LABELS = {
  RENEWAL_ACCEPT: 'Renewal Acceptance',
  MANUAL_RENEWAL: 'Renewal invite',
  AUTO_RENEWAL: 'Renewal invite',
  MTA_ACCEPT: 'Policy change',
  CANCELLATION: 'Cancellation',
  NB: 'New business',
  QUOTE: 'Quote'
};

// Tie-break only: when two transactions share a date, show them in the order the
// business asked for - acceptance above invite, above new business, above quote.
const TRANSACTION_RANK = {
  RENEWAL_ACCEPT: 0,
  MTA_ACCEPT: 1,
  MANUAL_RENEWAL: 2,
  AUTO_RENEWAL: 2,
  CANCELLATION: 3,
  NB: 4,
  QUOTE: 5
};

const ADDITIONAL_KEY = '__additional__';

const dominantType = docs => {
  const counts = docs.reduce((acc, doc) => {
    const code = get(doc, 'type.code');
    acc[code] = (acc[code] || 0) + 1;
    return acc;
  }, {});
  return Object.keys(counts).sort((a, b) => {
    if (counts[b] !== counts[a]) return counts[b] - counts[a];
    return (TRANSACTION_RANK[a] ?? 99) - (TRANSACTION_RANK[b] ?? 99);
  })[0];
};

// One collapsible container per transaction. Everything is kept - older terms are
// pushed to the bottom rather than dropped - so a customer can still reach any
// document that was ever issued on the policy.
export const buildSections = (documents, productCode) => {
  const renderable = documents.filter(doc => get(documentsType[get(doc, 'type.code')], get(doc, 'subType.code')));

  const byTransaction = renderable.reduce((acc, doc) => {
    const key = doc.transactionId || ADDITIONAL_KEY;
    (acc[key] = acc[key] || []).push(doc);
    return acc;
  }, {});

  return Object.entries(byTransaction)
    .map(([key, docs]) => {
      const type = dominantType(docs);
      // For Cover My Tools and Beauty the quote pack *is* the policy pack.
      const label =
        type === 'QUOTE' && [COVERMYTOOLS, BEAUTY].includes(productCode)
          ? 'Policy'
          : TRANSACTION_LABELS[type] || 'Policy';
      const latest = docs.reduce((max, doc) => Math.max(max, new Date(doc.created).getTime()), 0);
      return {
        key,
        type,
        latest,
        title:
          key === ADDITIONAL_KEY ? 'Additional documents' : `${label} - ${moment(latest).format('DD/MM/YYYY')}`,
        docs: [...docs].sort((a, b) => new Date(a.created) - new Date(b.created))
      };
    })
    .sort((a, b) => {
      // Manual uploads carry no transaction, so they always sit at the bottom.
      if (a.key === ADDITIONAL_KEY) return 1;
      if (b.key === ADDITIONAL_KEY) return -1;
      if (b.latest !== a.latest) return b.latest - a.latest;
      return (TRANSACTION_RANK[a.type] ?? 99) - (TRANSACTION_RANK[b.type] ?? 99);
    });
};

const DocumentSection = props => {
  const { title, defaultOpened, children } = props;
  const [opened, setOpened] = useState(Boolean(defaultOpened));
  const parent = useRef(null);

  useEffect(() => {
    if (parent.current) {
      autoAnimate(parent.current);
    }
  }, [parent]);

  return (
    <div className={sectionStyle} ref={parent}>
      <button type="button" className={sectionHeaderStyle} onClick={() => setOpened(!opened)}>
        <h3 className={sectionTitleStyle}>{title}</h3>
        <img className={sectionIconStyle} alt="arrow" src={opened ? arrowUp : arrowDown} />
      </button>
      {opened && <div className={sectionBodyStyle}>{children}</div>}
    </div>
  );
};

export const DocumentPolicyContainer = props => {
  const { policy, setIsLoading } = props;
  const firebase = useFirebase();
  const handlePolicyDocumentDownload = async (docId, downloadMethod) => {
    try {
      setIsLoading(true);
      const { base64Content, contentType, filename } = await downloadMethod(docId);
      const blob = convertToBlob(base64Content, contentType);
      setIsLoading(false);
      downloadFile(blob, filename);
    } catch (err) {
      console.error(err);
      setIsLoading(false);
    }
  };

  const sections = buildSections(policy.policyDocuments || [], policy.productCode);

  return (
    <div className={documentContainer}>
      <div className={documentTitle}>Documents</div>
      {sections.map((section, index) => (
        <DocumentSection key={section.key} title={section.title} defaultOpened={index === 0}>
          <div className={policyButtonsContainer}>
            {section.docs.map(doc => (
              <DocumentButton
                key={doc.id}
                name={getDocumentName(doc, 'POLICY', policy.productCode)}
                onClick={() =>
                  handlePolicyDocumentDownload(doc.id, () => firebase.doDownloadDocument(doc.id, 'POLICY'))
                }
              />
            ))}
          </div>
        </DocumentSection>
      ))}
    </div>
  );
};
